import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { todayIso } from "../../lib/finance";
import {
  CONFORMITY_LABELS, assetRowsFrom, assetRowsPayload, assetRowsProblem, emptyInvoiceForm, emptyOrderForm, emptyQuoteForm, emptyReceptionForm, invoicePayload,
  invoiceProblem, money, orderPayload, orderProblem, procurementError, quotePayload, quoteProblem, receptionPayload, receptionProblem, ASSET_TYPES,
} from "../../lib/procurement";
import PrivateFilesField from "../PrivateFilesField";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const FILE_CONTEXT = "procurement_document";

// The same shell for every action: one request, the busy / error states, the confirm button disabled while something is missing.
function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (call, onDone, success) => {
    setBusy(true);
    setError("");
    try {
      const data = await call();
      if (success) toast.success(success);
      onDone(data);
    } catch (e) {
      setError(procurementError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run, setError };
}

function Shell({ title, description, onClose, busy, error, problem, confirm, danger, onSubmit, testid, children }) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4">
          {children}
          {error && <p className="text-sm text-red-600" role="alert" data-testid="request-dialog-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant={danger ? "destructive" : "default"} onClick={onSubmit} disabled={busy || !!problem} title={problem || undefined} data-testid="request-dialog-submit">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ id, label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

// --- A reason, nothing else: reject, cancel, close, keep a dearer quote -------------------------------------------------------------
export function ReasonDialog({ title, description, label = "Motif", placeholder, confirm, danger, optional, onSubmit, onClose, onDone, success }) {
  const action = useAction();
  const [reason, setReason] = useState("");
  const problem = !optional && reason.trim().length < 3 ? "Indiquez le motif (au moins 3 caractères)." : "";
  return (
    <Shell title={title} description={description} onClose={onClose} busy={action.busy} error={action.error} problem={problem} confirm={confirm} danger={danger}
      testid="request-reason-dialog" onSubmit={() => action.run(() => onSubmit(reason.trim()), onDone, success)}>
      <Row id="request-reason" label={optional ? `${label} (facultatif)` : label}>
        <Textarea id="request-reason" rows={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={placeholder} data-testid="request-reason" />
      </Row>
    </Shell>
  );
}

// --- A quote -------------------------------------------------------------------------------------------------------------------------
export function QuoteDialog({ request, onClose, onDone }) {
  const action = useAction();
  const [form, setForm] = useState(emptyQuoteForm);
  const [files, setFiles] = useState([]);
  const [suppliers, setSuppliers] = useState(null);
  useEffect(() => {
    api.get("/procurement/supplier-options").then(({ data }) => setSuppliers(data.items)).catch(() => setSuppliers([]));
  }, []);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const problem = quoteProblem(form, request.currency);
  return (
    <Shell title="Ajouter un devis" description={`${request.number} — un seul devis suffit ; plusieurs permettent de comparer.`} onClose={onClose} busy={action.busy}
      error={action.error} problem={problem} confirm="Ajouter le devis" testid="quote-dialog"
      onSubmit={() => action.run(async () => (await api.post(`/procurement/requests/${request.id}/quotes`,
        quotePayload(form, request.currency, request.version, files.map((f) => f.id)))).data, onDone, "Devis ajouté")}>
      <Row id="quote-supplier" label="Fournisseur">
        <Select value={form.supplier_id || undefined} onValueChange={(supplier_id) => set({ supplier_id })} disabled={suppliers === null}>
          <SelectTrigger id="quote-supplier" className="h-10" aria-label="Fournisseur" data-testid="quote-supplier"><SelectValue placeholder={suppliers === null ? "Chargement…" : "Choisir…"} /></SelectTrigger>
          <SelectContent>
            {suppliers?.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">Aucun fournisseur actif. Demandez à la personne qui gère les achats d'en ajouter un.</div>}
            {suppliers?.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Row>
      <div className="grid gap-3 sm:grid-cols-2">
        <Row id="quote-amount" label={`Montant du devis (${request.currency})`}>
          <Input id="quote-amount" inputMode="decimal" value={form.amount} onChange={(e) => set({ amount: e.target.value })} className="h-10" data-testid="quote-amount" />
        </Row>
        <Row id="quote-valid" label="Valable jusqu'au (facultatif)">
          <Input id="quote-valid" type="date" value={form.valid_until} onChange={(e) => set({ valid_until: e.target.value })} className="h-10" data-testid="quote-valid-until" />
        </Row>
      </div>
      <Row id="quote-ref" label="Référence du devis (facultatif)">
        <Input id="quote-ref" value={form.reference} maxLength={120} onChange={(e) => set({ reference: e.target.value })} className="h-10" data-testid="quote-reference" />
      </Row>
      <PrivateFilesField context={FILE_CONTEXT} label="Le devis du fournisseur (PDF ou image)" files={files} setFiles={setFiles} testid="quote-file" />
      <Row id="quote-note" label="Note (facultative)">
        <Textarea id="quote-note" rows={2} maxLength={1000} value={form.note} onChange={(e) => set({ note: e.target.value })} data-testid="quote-note" />
      </Row>
    </Shell>
  );
}

// --- Order ---------------------------------------------------------------------------------------------------------------------------
export function OrderDialog({ request, onClose, onDone }) {
  const action = useAction();
  const today = todayIso();
  const [form, setForm] = useState(() => emptyOrderForm(today));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const problem = orderProblem(form, today);
  return (
    <Shell title="Passer la commande" description={`${request.number} — à faire une fois la commande passée auprès du fournisseur.`} onClose={onClose} busy={action.busy}
      error={action.error} problem={problem} confirm="Enregistrer la commande" testid="order-dialog"
      onSubmit={() => action.run(async () => (await api.post(`/procurement/requests/${request.id}/order`, orderPayload(form, request.version))).data, onDone, "Commande enregistrée")}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Row id="order-date" label="Commandé le"><Input id="order-date" type="date" max={today} value={form.ordered_on} onChange={(e) => set({ ordered_on: e.target.value })} className="h-10" data-testid="order-date" /></Row>
        <Row id="order-delivery" label="Livraison prévue le (facultatif)"><Input id="order-delivery" type="date" min={form.ordered_on} value={form.expected_delivery_on} onChange={(e) => set({ expected_delivery_on: e.target.value })} className="h-10" data-testid="order-delivery" /></Row>
      </div>
      <Row id="order-ref" label="Numéro du bon de commande (facultatif)"><Input id="order-ref" value={form.reference} maxLength={120} onChange={(e) => set({ reference: e.target.value })} className="h-10" data-testid="order-reference" /></Row>
    </Shell>
  );
}

// --- Delivery ------------------------------------------------------------------------------------------------------------------------
export function ReceptionDialog({ request, onClose, onDone }) {
  const action = useAction();
  const today = todayIso();
  const [form, setForm] = useState(() => emptyReceptionForm(request, today));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLine = (index, quantity) => setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === index ? { ...l, quantity } : l)) }));
  const problem = receptionProblem(form, today, request.order?.ordered_on);
  return (
    <Shell title="Enregistrer une livraison" description={`${request.number} — indiquez ce qui est réellement arrivé ; le reste reste attendu.`} onClose={onClose} busy={action.busy}
      error={action.error} problem={problem} confirm="Enregistrer la livraison" testid="reception-dialog"
      onSubmit={() => action.run(async () => (await api.post(`/procurement/requests/${request.id}/receptions`, receptionPayload(form, request.version))).data, onDone, "Livraison enregistrée")}>
      <Row id="reception-date" label="Reçu le"><Input id="reception-date" type="date" max={today} value={form.received_on} onChange={(e) => set({ received_on: e.target.value })} className="h-10" data-testid="reception-date" /></Row>
      <div className="space-y-2" data-testid="reception-lines">
        <Label>Quantités reçues</Label>
        {form.lines.map((line, index) => (
          <div key={line.line_id} className="flex items-center gap-3">
            <span className="min-w-0 flex-1 text-sm text-slate-800 break-words">{line.label}<span className="block text-xs text-slate-500">Reste à recevoir : {line.remaining}</span></span>
            <Input inputMode="numeric" value={line.quantity} onChange={(e) => setLine(index, e.target.value)} className="h-10 w-20 text-right" aria-label={`Quantité reçue : ${line.label}`} data-testid={`reception-qty-${index}`} />
          </div>
        ))}
      </div>
      <Row id="reception-conformity" label="La livraison est-elle conforme à la commande ?">
        <Select value={form.conformity} onValueChange={(conformity) => set({ conformity })}>
          <SelectTrigger id="reception-conformity" className="h-10" aria-label="Conformité" data-testid="reception-conformity"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(CONFORMITY_LABELS).map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      {form.conformity === "claim" && (
        <Row id="reception-note" label="Ce qui est à réclamer"><Textarea id="reception-note" rows={2} maxLength={1000} value={form.note} onChange={(e) => set({ note: e.target.value })} data-testid="reception-note" /></Row>
      )}
      <Row id="reception-by" label="Reçu par (facultatif)" hint="Par défaut : vous."><Input id="reception-by" value={form.received_by_name} maxLength={120} onChange={(e) => set({ received_by_name: e.target.value })} className="h-10" placeholder="Nom de la personne qui a réceptionné" data-testid="reception-by" /></Row>
    </Shell>
  );
}

// --- Assets made from what arrived ---------------------------------------------------------------------------------------------------
export function AssetsDialog({ request, onClose, onDone }) {
  const action = useAction();
  const [rows, setRows] = useState(() => assetRowsFrom(request));
  const setRow = (key, patch) => setRows((list) => list.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const problem = assetRowsProblem(rows);
  return (
    <Shell title="Créer des actifs depuis la livraison" description="Rien n'est créé tout seul : cochez ce qui doit entrer dans l'inventaire. Le propriétaire reste « à confirmer » et le financeur « inconnu » — vous les précisez ensuite sur chaque actif."
      onClose={onClose} busy={action.busy} error={action.error} problem={problem} confirm="Créer les actifs" testid="assets-dialog"
      onSubmit={() => action.run(async () => (await api.post(`/procurement/requests/${request.id}/assets`, assetRowsPayload(rows, request.version))).data, onDone, "Actifs ajoutés à l'inventaire")}>
      <div className="space-y-3 max-h-[50vh] overflow-auto pr-1" data-testid="asset-rows">
        {rows.map((row, index) => (
          <div key={row.key} className={`rounded-md border p-3 space-y-2 ${row.include ? "border-jamora/40 bg-jamora/5" : "border-slate-200"}`} data-testid={`asset-row-${index}`}>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
              <Checkbox checked={row.include} onCheckedChange={(v) => setRow(row.key, { include: v === true })} aria-label={`Créer l'actif : ${row.label}`} data-testid={`asset-row-include-${index}`} />
              {row.label}
            </label>
            {row.include && (
              <div className="grid gap-2 sm:grid-cols-2">
                <Select value={row.type} onValueChange={(type) => setRow(row.key, { type })}>
                  <SelectTrigger className="h-10" aria-label="Type d'actif"><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSET_TYPES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={row.serial_number} maxLength={120} onChange={(e) => setRow(row.key, { serial_number: e.target.value })} className="h-10" placeholder="Numéro de série" aria-label="Numéro de série" data-testid={`asset-row-serial-${index}`} />
                <Input value={row.brand} maxLength={120} onChange={(e) => setRow(row.key, { brand: e.target.value })} className="h-10" placeholder="Marque" aria-label="Marque" />
                <Input value={row.model} maxLength={120} onChange={(e) => setRow(row.key, { model: e.target.value })} className="h-10" placeholder="Modèle" aria-label="Modèle" />
                <Input inputMode="decimal" value={row.cost} onChange={(e) => setRow(row.key, { cost: e.target.value })} className="h-10 sm:col-span-2" placeholder={`Coût (${row.currency})`} aria-label={`Coût en ${row.currency}`} />
              </div>
            )}
          </div>
        ))}
      </div>
    </Shell>
  );
}

// --- Invoice -------------------------------------------------------------------------------------------------------------------------
export function InvoiceDialog({ request, onClose, onDone }) {
  const action = useAction();
  const today = todayIso();
  const [form, setForm] = useState(() => emptyInvoiceForm(today, request));
  const [files, setFiles] = useState([]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const problem = invoiceProblem(form, request.currency, today, files);
  return (
    <Shell title="Saisir la facture" description={`${request.number} — la facture du fournisseur. Une seule par demande : complète, ou dès qu'une partie est reçue (la suite de la livraison s'enregistre ensuite).`} onClose={onClose} busy={action.busy}
      error={action.error} problem={problem} confirm="Enregistrer la facture" testid="invoice-dialog"
      onSubmit={() => action.run(async () => (await api.post(`/procurement/requests/${request.id}/invoice`,
        invoicePayload(form, request.currency, request.version, files.map((f) => f.id)))).data, onDone, "Facture enregistrée")}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Row id="invoice-ref" label="Référence de la facture"><Input id="invoice-ref" value={form.reference} maxLength={120} onChange={(e) => set({ reference: e.target.value })} className="h-10" data-testid="invoice-reference" /></Row>
        <Row id="invoice-date" label="Date de la facture"><Input id="invoice-date" type="date" max={today} value={form.issued_on} onChange={(e) => set({ issued_on: e.target.value })} className="h-10" data-testid="invoice-date" /></Row>
      </div>
      <Row id="invoice-amount" label={`Montant facturé (${request.currency})`}
        hint={request.quotes.find((q) => q.selected) ? `Devis retenu : ${money(request.quotes.find((q) => q.selected).amount_minor, request.currency)}. Un écart est signalé, pas bloqué.` : undefined}>
        <Input id="invoice-amount" inputMode="decimal" value={form.amount} onChange={(e) => set({ amount: e.target.value })} className="h-10" data-testid="invoice-amount" />
      </Row>
      <PrivateFilesField context={FILE_CONTEXT} label="La facture (PDF ou image)" files={files} setFiles={setFiles} testid="invoice-file" />
    </Shell>
  );
}

// --- The single expense in Finance ---------------------------------------------------------------------------------------------------
export function ExpenseDialog({ request, onClose, onDone }) {
  const action = useAction();
  const [categories, setCategories] = useState(null);
  const [category, setCategory] = useState("");
  useEffect(() => {
    api.get("/finance/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  }, []);
  return (
    <Shell title="Enregistrer la dépense" description="Crée un brouillon de dépense dans les finances, avec la facture saisie. Qui a payé et le justificatif se renseignent là-bas ; une demande n'a jamais qu'une seule dépense."
      onClose={onClose} busy={action.busy} error={action.error} problem={category ? "" : "Choisissez la catégorie de dépense."} confirm="Créer le brouillon de dépense" testid="expense-dialog"
      onSubmit={() => action.run(async () => (await api.post(`/procurement/requests/${request.id}/expense`, { expected_version: request.version, category_id: category })).data, onDone, "Dépense créée dans les finances")}>
      <Row id="expense-category" label="Catégorie de dépense">
        <Select value={category || undefined} onValueChange={setCategory} disabled={categories === null}>
          <SelectTrigger id="expense-category" className="h-10" aria-label="Catégorie" data-testid="expense-category"><SelectValue placeholder={categories === null ? "Chargement…" : "Choisir…"} /></SelectTrigger>
          <SelectContent>{categories?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <p className="text-sm text-slate-600">
        {request.invoice && <>Facture <strong>{request.invoice.reference}</strong> · {money(request.invoice.amount_minor, request.currency)}.</>}
      </p>
    </Shell>
  );
}


// --- A plain confirmation that says what will happen ---------------------------------------------------------------------------------
export function ConfirmDialog({ title, description, confirm, danger, onSubmit, onClose, onDone, success }) {
  const action = useAction();
  return (
    <Shell title={title} description={description} onClose={onClose} busy={action.busy} error={action.error} problem="" confirm={confirm} danger={danger}
      testid="request-confirm-dialog" onSubmit={() => action.run(onSubmit, onDone, success)}>
      <span className="sr-only">Confirmation</span>
    </Shell>
  );
}
